import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useEffect, lazy, Suspense } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';
import { CallProvider } from './context/CallContext';
import { CallOverlay } from './components/CallOverlay';
import { NotificationService } from './services/NotificationService';
import { authApi } from './api/client';
import OfflineIndicator from './components/OfflineIndicator';

// Layout
import AppLayout from './components/AppLayout';
import GoogleAutoAuth from './components/GoogleAutoAuth';

// Pages - Auth
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));

// Pages - Main
const ChatsPage = lazy(() => import('./pages/chats/ChatsPage'));
const ChatWindowPage = lazy(() => import('./pages/chats/ChatWindowPage'));
const ShopFeedPage = lazy(() => import('./pages/shops/ShopFeedPage'));
const ShopPage = lazy(() => import('./pages/shops/ShopPage'));
const CommunitiesPage = lazy(() => import('./pages/communities/CommunitiesPage'));
const CommunityPage = lazy(() => import('./pages/communities/CommunityPage'));
const OrdersPage = lazy(() => import('./pages/orders/OrdersPage'));
const OrderDetailPage = lazy(() => import('./pages/orders/OrderDetailPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const SubscribePage = lazy(() => import('./pages/profile/SubscribePage'));
const SettingsPage = lazy(() => import('./pages/profile/SettingsPage'));
const PrivacyPage = lazy(() => import('./pages/profile/PrivacyPage'));
const RateAppPage = lazy(() => import('./pages/profile/RateAppPage'));
const HelpSupportPage = lazy(() => import('./pages/profile/HelpSupportPage'));

// Pages - Dashboard (Shop Owner)
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const ShopSettingsPage = lazy(() => import('./pages/dashboard/ShopSettingsPage'));
const ProductsManagerPage = lazy(() => import('./pages/dashboard/ProductsManagerPage'));
const CustomerCRMPage = lazy(() => import('./pages/dashboard/CustomerCRMPage'));
const ParchiManagerPage = lazy(() => import('./pages/dashboard/ParchiManagerPage'));
const BroadcastPage = lazy(() => import('./pages/dashboard/BroadcastPage'));
const LedgerPage = lazy(() => import('./pages/dashboard/LedgerPage'));
const ReportsPage = lazy(() => import('./pages/dashboard/ReportsPage'));
const ModerationPage = lazy(() => import('./pages/dashboard/ModerationPage'));

// Pages - Onboarding
const CreateShopPage = lazy(() => import('./pages/onboarding/CreateShopPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));

// PWA & Public Pages
const ProductPublicPage = lazy(() => import('./pages/shops/ProductPublicPage'));
const FavouritesPage = lazy(() => import('./pages/shops/FavouritesPage'));
const MarketSearchPage = lazy(() => import('./pages/shops/MarketSearchPage'));

function ProtectedRoute({ children }) {
  const { isAuth, loading, openAuthModal } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="loading-spinner" /></div>;
  if (!isAuth) {
    openAuthModal('access this page');
    return <Navigate to="/shops" replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { isAuth, loading } = useAuth();
  if (loading) return null;
  return !isAuth ? children : <Navigate to="/shops" replace />;
}

function AppRoutes() {
  const navigate = useNavigate();
  const { isAuth } = useAuth();

  useEffect(() => {
    // Expose native receiver callback for the Android App WebView
    window.onFcmTokenReceived = (token) => {
      console.log('[NativeBridge] FCM Token from Android shell refreshed:', token);
      if (token) {
        sessionStorage.setItem('android_fcm_token', token);
        if (isAuth) {
          console.log('[NativeBridge] User authenticated, executing dynamic register...');
          NotificationService.registerDeviceToken();
        }
      }
    };

    // If already logged in, check if we can register the device immediately
    if (isAuth) {
      console.log('[NativeBridge] User authenticated, executing device registration...');
      NotificationService.registerDeviceToken();
    }
  }, [isAuth]);

  useEffect(() => {
    // 1. Helper to route deep links from native intents to React Router routes
    const handleDeepLink = (urlOrPath) => {
      if (!urlOrPath) return;
      console.log('[DeepLink] Processing:', urlOrPath);

      let path = urlOrPath;

      // Extract path if it contains custom protocol or scheme (e.g. shopaxo://chat/123 or https://...)
      if (urlOrPath.includes(':/')) {
        const schemeEndIndex = urlOrPath.indexOf(':/');
        const rest = urlOrPath.substring(schemeEndIndex + 2).replace(/^\/+/, '');
        
        if (urlOrPath.startsWith('http')) {
          const slashIndex = rest.indexOf('/');
          path = slashIndex !== -1 ? rest.substring(slashIndex) : '/';
        } else {
          path = '/' + rest;
        }
      }

      // Normalize native paths to match plural React Router paths
      let targetPath = path;
      if (path.startsWith('/chat/')) {
        targetPath = path.replace('/chat/', '/chats/');
      } else if (path.startsWith('/order/')) {
        targetPath = path.replace('/order/', '/orders/');
      } else if (path.startsWith('/customer/')) {
        targetPath = path.replace('/customer/', '/dashboard/customers/');
      } else if (path.startsWith('/product/')) {
        targetPath = path.replace('/product/', '/dashboard/products/');
      } else if (path.startsWith('/ledger/')) {
        const id = path.split('/').pop();
        targetPath = `/dashboard/ledger?userId=${id}`;
      }

      console.log('[DeepLink] Navigating React app to:', targetPath);
      navigate(targetPath);
    };

    // 2. Handle cold boot deep link if MainActivity passed it via sessionStorage
    const pendingNav = sessionStorage.getItem('db_pending_navigation');
    if (pendingNav) {
      sessionStorage.removeItem('db_pending_navigation');
      console.log('[DeepLink] Found pending navigation in sessionStorage:', pendingNav);
      handleDeepLink(pendingNav);
    }

    NotificationService.init(navigate);

    if (Capacitor.isNativePlatform()) {

      // Register Capacitor listener for deep links (warm/cold starts while app is active)
      const urlListener = CapApp.addListener('appUrlOpen', (event) => {
        console.log('[DeepLink] appUrlOpen event received:', event.url);
        handleDeepLink(event.url);
      });

      // Hardware back button handler
      const backListener = CapApp.addListener('backButton', (data) => {
        const path = window.location.pathname;
        const exitPaths = ['/chats', '/shops', '/dashboard', '/communities', '/orders', '/profile', '/login', '/register', '/'];
        if (exitPaths.includes(path)) {
          CapApp.exitApp();
        } else {
          if (data.canGoBack && window.history.state && window.history.state.idx > 0 && window.history.length > 1) {
            navigate(-1);
          } else {
            // Fallback navigation based on path
            if (path.startsWith('/chats/')) {
              navigate('/chats');
            } else if (path.startsWith('/communities/')) {
              navigate('/communities');
            } else if (path.startsWith('/orders/')) {
              navigate('/orders');
            } else if (path.startsWith('/shops/')) {
              navigate('/shops');
            } else if (path.startsWith('/dashboard/')) {
              if (path === '/dashboard/ledger') {
                navigate('/dashboard/customers');
              } else {
                navigate('/dashboard');
              }
            } else if (path === '/favourites') {
              navigate('/profile');
            } else if (path === '/market') {
              navigate('/shops');
            } else if (path === '/subscribe') {
              navigate('/profile');
            } else if (path === '/start-shop') {
              navigate('/profile');
            } else {
              navigate('/shops');
            }
          }
        }
      });

      // App resume event for session check
      const resumeListener = CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          console.log('App resumed. Checking session integrity...');
          // Check for any deep links generated while app was suspended
          const resumePending = sessionStorage.getItem('db_pending_navigation');
          if (resumePending) {
            sessionStorage.removeItem('db_pending_navigation');
            handleDeepLink(resumePending);
          }
        }
      });

      return () => {
        urlListener.then(h => h.remove());
        backListener.then(h => h.remove());
        resumeListener.then(h => h.remove());
      };
    }
  }, [navigate]);

  return (
    <>
      <OfflineIndicator />
      <Suspense fallback={
        <div className="flex items-center justify-center h-screen">
          <div className="loading-spinner" />
        </div>
      }>
        <Routes>
          {/* Auth & Landing Routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/landing" element={<LandingPage />} />

          {/* App Layout Routes (Public Feed & Protected Actions) */}
          <Route element={<AppLayout />}>
            <Route path="/" element={<Navigate to="/shops" replace />} />
            <Route path="shops" element={<ShopFeedPage />} />
            <Route path="shops/:slug" element={<ShopPage />} />
            <Route path="s/:slug" element={<ShopPage />} />
            <Route path="p/:productId" element={<ProductPublicPage />} />
            <Route path="p/:slug/:productId" element={<ProductPublicPage />} />
            <Route path="market" element={<MarketSearchPage />} />

            {/* Protected Routes */}
            <Route path="chats" element={<ProtectedRoute><ChatsPage /></ProtectedRoute>} />
            <Route path="chats/:id" element={<ProtectedRoute><ChatWindowPage /></ProtectedRoute>} />
            <Route path="favourites" element={<ProtectedRoute><FavouritesPage /></ProtectedRoute>} />
            <Route path="communities" element={<ProtectedRoute><CommunitiesPage /></ProtectedRoute>} />
            <Route path="communities/:id" element={<ProtectedRoute><CommunityPage /></ProtectedRoute>} />
            <Route path="orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
            <Route path="orders/:id" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
            <Route path="profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="subscribe" element={<ProtectedRoute><SubscribePage /></ProtectedRoute>} />
            <Route path="settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="privacy" element={<ProtectedRoute><PrivacyPage /></ProtectedRoute>} />
            <Route path="rate" element={<ProtectedRoute><RateAppPage /></ProtectedRoute>} />
            <Route path="help" element={<ProtectedRoute><HelpSupportPage /></ProtectedRoute>} />
            <Route path="start-shop" element={<ProtectedRoute><CreateShopPage /></ProtectedRoute>} />

            {/* Dashboard Routes */}
            <Route path="dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="dashboard/settings" element={<ProtectedRoute><ShopSettingsPage /></ProtectedRoute>} />
            <Route path="dashboard/products" element={<ProtectedRoute><ProductsManagerPage /></ProtectedRoute>} />
            <Route path="dashboard/customers" element={<ProtectedRoute><CustomerCRMPage /></ProtectedRoute>} />
            <Route path="dashboard/parchis" element={<ProtectedRoute><ParchiManagerPage /></ProtectedRoute>} />
            <Route path="dashboard/broadcasts" element={<ProtectedRoute><BroadcastPage /></ProtectedRoute>} />
            <Route path="dashboard/ledger" element={<ProtectedRoute><LedgerPage /></ProtectedRoute>} />
            <Route path="dashboard/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
            <Route path="dashboard/moderation" element={<ProtectedRoute><ModerationPage /></ProtectedRoute>} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <GoogleAutoAuth />
        <ChatProvider>
          <CallProvider>
            <AppRoutes />
            <CallOverlay />
            <Toaster
              position="top-center"
              toastOptions={{
                style: {
                  background: '#16161F',
                  color: '#F0F0F8',
                  border: '1px solid #2A2A38',
                  borderRadius: '12px',
                  fontSize: '14px',
                },
                success: { iconTheme: { primary: '#22C55E', secondary: '#fff' } },
                error: { iconTheme: { primary: '#EF4444', secondary: '#fff' } },
              }}
            />
          </CallProvider>
        </ChatProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
